<template>
  <PForm
    ref="pForm"
    :heading="
      '1-Click Bulk Process Payment Requests (' +
      selectedPRsForOneClickBulkProcess.length +
      ')'
    "
    :primary-cta="oneClickProcessButton"
    :secondary-cta="secondaryCta"
    @close="onClose"
  >
    <PMarkupTable
      :columns="columns"
      :rows="selectedPRsForOneClickBulkProcess"
      class-name="full-width"
      flat
      table-header-class="text-neutral-100"
    />

    <div
      class="flex full-width text-right text-weight-medium q-my-md justify-end"
    >
      <div>
        <p class="text-right text-weight-medium q-my-md">
          Total Amount
          <span
            class="block text-right text-xxs text-neutral-70 text-weight-regular"
          >
            Transfer Amount for this batch
          </span>
        </p>
      </div>
      <div class="q-mx-md">
        <p class="text-right text-weight-medium q-my-md">
          {{ numberFormat({ number: totalNetPayable, style: "currency" }) }}
        </p>
      </div>
    </div>
    <template #extra-note>
      <p
        v-if="!isEmpty(computedExtraNote)"
        class="q-mr-xxl q-ml-xxl flex text-weight-medium justify-end text-neutral text-xs text-italic"
      >
        {{ computedExtraNote.text }}
      </p>
    </template>
  </PForm>
</template>

<script setup>
import { useChangeCase } from "@vueuse/integrations/useChangeCase";

import oneClickBulkProcessForm from "../models/oneClickBulkProcessForm";

import { numberFormat } from "src/assets/scripts/utilities/helpers";

import { useOneClickProcess } from "../composables/one-click-process";
import { usePaymentRequestStore } from "src/stores/payment-request";
import { unref } from "vue";
import { isEmpty } from "assets/scripts/utilities/helpers";

const props = defineProps({
  selectedPRsForOneClickBulkProcess: {
    type: Array,
    default: () => [],
  },
  resetSelectedPRs: {
    type: Function,
    default: () => {},
  },
});

const { oneClickBulkProcessDrawerState, toggleOneClickBulkProcessDrawer } =
  usePaymentRequestStore();

const pForm = ref(null);

const { isLoading, closeForm } = useForm({
  formModel: oneClickBulkProcessForm(),
  entity: props.selectedPRsForOneClickBulkProcess,
  formRef: pForm,
  isForm: oneClickBulkProcessDrawerState,
  toggleForm: toggleOneClickBulkProcessDrawer,
});

const computedSelectedPrs = computed(() => {
  return props.selectedPRsForOneClickBulkProcess;
});

const totalNetPayable = computed(() => {
  return unref(computedSelectedPrs)
    .filter((pr) => pr.net_payable)
    .reduce((total, pr) => +total + +pr.net_payable, 0);
});

const { oneClickProcessButton, computedOneClickProcess } = useOneClickProcess({
  closeForm,
  isLoading,
  label: "1-Click Bulk Process Payment",
  totalNetPayable,
  selectedPRs: computedSelectedPrs,
  resetSelection: props.resetSelectedPRs,
  componentProps: {
    title: "Bulk Process Payment",
  },
});

const onClose = () => {
  try {
    closeForm(true);
  } catch (error) {
    console.error("error", error);
  }
};

const secondaryCta = computed(() => {
  return {};
});

let columns = [
  {
    align: "left",
    name: "internal_number",
    label: "ID",
    field: "internal_number",
  },
  {
    align: "left",
    name: "seller_info",
    label: "Vendor",
    field: "seller_info.shop_name",
    format: (_value, row) => {
      return `<span class="block">
            ${useChangeCase(row?.seller_info?.shop_name, "capitalCase").value}
          </span>

          <span class="block text-neutral-70">${
            row?.seller_info?.gstin ?? ""
          }</span>`;
    },
  },
  {
    align: "left",
    name: "bank_details",
    label: "Bank Details",
    field: "seller_info.bank_name",
    format: (_value, row) => {
      return `<span class="block">
            ${row?.seller_info?.bank_name ?? ""}
          </span>
          <span class="block text-neutral-70">${
            row?.seller_info?.bank_account_number ?? ""
          }</span>`;
    },
  },
  {
    align: "left",
    name: "bill_number",
    label: "Bill#",
    field: "bill_number",
    format: (value) => value || "-",
  },
  {
    align: "left",
    name: "invc_number",
    label: "INVC#",
    field: "invc_number",
    format: (value) => value || "-",
  },
  {
    align: "left",
    name: "net_payable",
    label: "Transfer Amount",
    field: "net_payable",
    format: (value) => numberFormat({ number: value, style: "currency" }),
  },
];

const computedExtraNote = computed(() => {
  if (!unref(computedOneClickProcess).allowed) {
    return {
      text: `${unref(computedOneClickProcess).extraNote}`,
    };
  }

  return {};
});
</script>
